import React from 'react';
import { Helmet } from 'react-helmet';
import './legal.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Privacy Policy — dmyor</title>
        <link rel="canonical" href="https://www.dmyor.com/privacy-policy" />
        <meta name="description" content="How dmyor collects, uses, shares, and protects your personal data, including Google sign-in data and AI processing." />
        <meta property="og:title" content="Privacy Policy — dmyor" />
        <meta property="og:description" content="How dmyor collects, uses, shares, and protects your personal data." />
        <meta property="og:image" content="https://dmyor.com/images/og-image.png" />
        <meta property="og:url" content="https://www.dmyor.com/privacy-policy" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="legal-header">
        <div className="legal-eyebrow">dmyor</div>
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: 28 June 2026</p>
      </div>

      <div className="legal-body">
        <p>
          This Privacy Policy explains how dmyor ("dmyor", "we", "us") collects, uses,
          shares, and protects your personal data when you use our website and services
          (the "Service"). It applies to users worldwide, including users in the European
          Economic Area (EEA), the United Kingdom, and the United States. By using the
          Service, you agree to this Policy. If you do not agree, please do not use the Service.
        </p>

        <h2>1. Who we are (Data Controller)</h2>
        <p>
          dmyor is the controller responsible for your personal data. For any privacy
          question or to exercise your rights, contact us at{' '}
          <a href="mailto:support@dmyor.com">support@dmyor.com</a>.
        </p>

        <h2>2. Information we collect</h2>
        <ul>
          <li>
            <strong>Account information.</strong> Your name, email address, and (optionally)
            country and date of birth. If you sign in with Google, we receive your name,
            email address, and Google account identifier (see Section 3).
          </li>
          <li>
            <strong>Content you create.</strong> The visions, milestones, actions, notes,
            documents, habits, todos, journal entries, side quests, and any updates you add.
            Some of this content is private by default and only becomes public if you choose
            to release or share it.
          </li>
          <li>
            <strong>Usage and device data.</strong> Basic technical data such as IP address,
            browser type, pages viewed, and timestamps, used to operate and secure the Service.
          </li>
          <li>
            <strong>Cookies.</strong> A secure authentication cookie to keep you signed in,
            and limited functional/analytics cookies (see Section 8).
          </li>
          <li>
            <strong>Payment data.</strong> If you buy credits, payment is processed by a
            third-party payment provider. We do not store your full card details.
          </li>
        </ul>

        <h2>3. Google sign-in and Limited Use</h2>
        <p>
          If you choose "Continue with Google", we use Google OAuth to authenticate you and
          receive your basic profile (name, email address, and Google account ID). We use this
          data only to create and secure your account and to sign you in. We request the minimum
          scopes necessary and do not access your Gmail, Drive, contacts, or other Google data.
        </p>
        <p>
          dmyor's use and transfer of information received from Google APIs adheres to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
            Google API Services User Data Policy
          </a>, including the Limited Use requirements. We do not sell Google user data and do
          not use it for advertising.
        </p>

        <h2>4. How we use your information</h2>
        <ul>
          <li>To create, operate, secure, and support your account.</li>
          <li>To provide the planning, execution, habit, document, and journal features.</li>
          <li>
            To power AI features ("Dmyor Assist"). When you use AI features, the relevant parts
            of your content are sent to a third-party AI provider to generate suggestions,
            breakdowns, summaries, and documents (see Section 6).
          </li>
          <li>To communicate with you about the Service and, if you opt in, product updates.</li>
          <li>To detect, prevent, and address fraud, abuse, and security issues.</li>
          <li>To comply with legal obligations.</li>
        </ul>

        <h2>5. Legal bases (EEA/UK users)</h2>
        <p>
          Where the GDPR or UK GDPR applies, we process your data on these legal bases:
          <strong> performance of a contract</strong> (to provide the Service you request);
          <strong> legitimate interests</strong> (to secure, improve, and support the Service);
          <strong> consent</strong> (for optional marketing emails and non-essential cookies,
          which you can withdraw at any time); and <strong> legal obligation</strong> (to comply
          with applicable law).
        </p>

        <h2>6. Sharing and service providers</h2>
        <p>
          We do not sell your personal data. We share data only with service providers
          ("processors") who help us run the Service, under contracts that require them to
          protect it:
        </p>
        <ul>
          <li><strong>Cloud hosting and database</strong> providers that store and serve the application.</li>
          <li><strong>AI/LLM providers</strong> that process the content you submit to AI features to generate responses.</li>
          <li><strong>Email</strong> providers for account and (opt-in) product emails.</li>
          <li><strong>Payment</strong> providers for credit purchases.</li>
          <li><strong>Authentication</strong> (Google) when you choose Google sign-in.</li>
        </ul>
        <p>
          We may also disclose data if required by law, to enforce our Terms, or to protect
          the rights, safety, and security of users and the public.
        </p>

        <h2>7. International data transfers</h2>
        <p>
          dmyor operates globally and our providers may process data in countries other than
          your own, including outside the EEA/UK. Where required, we rely on appropriate
          safeguards (such as Standard Contractual Clauses) for these transfers.
        </p>

        <h2>8. Cookies</h2>
        <p>
          We use a strictly necessary authentication cookie so you can stay signed in. We may
          use limited analytics to understand usage. You can control non-essential cookies
          through the cookie banner and your browser settings. Disabling the authentication
          cookie will prevent you from signing in.
        </p>

        <h2>9. Data retention</h2>
        <p>
          We keep your personal data for as long as your account is active and as needed to
          provide the Service. If you delete your account, we delete or anonymise your personal
          data within a reasonable period, except where we must retain certain records to comply
          with legal, tax, or security obligations. Content you released publicly may persist as
          part of the public archive unless you remove it before deletion.
        </p>

        <h2>10. Security</h2>
        <p>
          We use reasonable technical and organisational measures to protect your data,
          including encrypted transport (HTTPS), hashed passwords, and access controls. No method
          of transmission or storage is completely secure, and we cannot guarantee absolute security.
        </p>

        <h2>11. Your rights</h2>
        <p>
          Depending on where you live, you may have the right to access, correct, delete, export
          (portability), restrict, or object to our processing of your personal data, and to
          withdraw consent. EEA/UK users may lodge a complaint with their local data protection
          authority. California residents have rights under the CCPA/CPRA, including the right to
          know, delete, correct, and opt out of "sale" or "sharing" of personal information; we do
          not sell or share personal information as those terms are defined. We will not discriminate
          against you for exercising your rights.
        </p>
        <p>
          To exercise any right, email <a href="mailto:support@dmyor.com">support@dmyor.com</a> or
          use the controls in your Account settings.
        </p>

        <h2>12. Account and data deletion</h2>
        <p>
          You can request deletion of your account and associated personal data at any time from
          your Account settings or by emailing <a href="mailto:support@dmyor.com">support@dmyor.com</a>.
          This permanently removes your visions and related content, subject to the retention
          exceptions in Section 9.
        </p>

        <h2>13. Children</h2>
        <p>
          The Service is not directed to children under 16 (or the minimum age required in your
          country). We do not knowingly collect personal data from children. If you believe a
          child has provided us data, contact us and we will delete it.
        </p>

        <h2>14. Changes to this Policy</h2>
        <p>
          We may update this Policy from time to time. We will post the updated version here with
          a new "Last updated" date and, for significant changes, notify you by email or in-app.
        </p>

        <h2>15. Contact</h2>
        <p>
          For privacy questions or requests, contact us at{' '}
          <a href="mailto:support@dmyor.com">support@dmyor.com</a>.
        </p>
      </div>
    </div>
  );
};

export default Privacy;
